import {
  AI_PROVIDER_LABELS,
  AI_PROVIDERS,
  type AiProvider,
  HOSTED_MODEL_LABEL,
  HOSTED_MODEL_PRICING,
} from '@agentic-flow-studio/shared/lib/ai-providers';
import { Cloud, Coins, KeyRound, Loader2, Sparkles, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { PasswordInput } from '../../ui/password-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

type SavedKey = {
  provider: AiProvider;
  keyLast4: string;
  isActive: boolean;
  validatedAt: number;
  connectedByEmail: string | null;
};

/**
 * BYOK settings, shared by both portals (Skill Viper chat and Inbox Clinic's analysis calls both
 * resolve through the same workspace key -- see `backend/services/aiProvider.service.ts`). A
 * pasted key is validated live against the real provider before it's ever saved
 * (`trpc.aiProviderKeys.save`), and this component never sees a key back from the server after
 * that -- only `keyLast4` for display.
 *
 * Two panels, side by side: the hosted key (what we charge per token, always available) and BYOK
 * (pick a provider, paste a key, activate it). Exactly one of them is the workspace's live source
 * at any time -- the banner above both states which, and the "Active" badge repeats it on
 * whichever card is currently live.
 */
export function AiProviderKeysSection() {
  const utils = trpc.useUtils();
  const { data: keys, isLoading } = trpc.aiProviderKeys.list.useQuery();
  const invalidate = () => utils.aiProviderKeys.list.invalidate();

  const savedKeys = keys ?? [];
  const activeKey = savedKeys.find((key) => key.isActive);

  const saveMutation = trpc.aiProviderKeys.save.useMutation({
    onSuccess: (_, variables) => {
      toast.success(`${AI_PROVIDER_LABELS[variables.provider]} key saved.`);
      invalidate();
    },
    onError: (error) => toast.error(error.message),
  });
  const setActiveMutation = trpc.aiProviderKeys.setActive.useMutation({
    onSuccess: (_, variables) => {
      toast.success(`${AI_PROVIDER_LABELS[variables.provider]} is now active.`);
      invalidate();
    },
    onError: (error) => toast.error(error.message),
  });
  const clearActiveMutation = trpc.aiProviderKeys.clearActive.useMutation({
    onSuccess: () => {
      toast.success('Switched back to the hosted key.');
      invalidate();
    },
    onError: (error) => toast.error(error.message),
  });
  const removeMutation = trpc.aiProviderKeys.remove.useMutation({
    onSuccess: (_, variables) => {
      toast.success(`${AI_PROVIDER_LABELS[variables.provider]} key removed.`);
      invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const isBusy =
    setActiveMutation.isPending || clearActiveMutation.isPending || removeMutation.isPending;

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm overflow-hidden">
      <CardHeader className="border-b border-border/50 bg-muted/30">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" />
          <CardTitle>AI Provider</CardTitle>
        </div>
        <CardDescription>
          Choose whether chat and analysis run on our hosted key or a provider key you bring
          yourself.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className="rounded-lg border border-border/60 bg-background/60 px-4 py-3 text-sm flex items-center gap-2">
          <span className="text-muted-foreground">Currently configured:</span>
          <span className="font-medium text-foreground">
            {activeKey
              ? `${AI_PROVIDER_LABELS[activeKey.provider]} (your key)`
              : `${HOSTED_MODEL_LABEL} (hosted)`}
          </span>
        </div>

        <div className="grid gap-4 lg:grid-cols-2 items-start">
          <HostedProviderCard
            isActive={!activeKey}
            isBusy={isBusy}
            onUseHosted={() => clearActiveMutation.mutate()}
          />
          <ByokPanel
            savedKeys={savedKeys}
            isLoading={isLoading}
            isBusy={isBusy}
            isSaving={saveMutation.isPending}
            savingProvider={saveMutation.variables?.provider}
            onSave={(provider, apiKey) => saveMutation.mutate({ provider, apiKey })}
            onSetActive={(provider) => setActiveMutation.mutate({ provider })}
            onRemove={(provider) => removeMutation.mutate({ provider })}
          />
        </div>
      </CardContent>
    </Card>
  );
}

interface HostedProviderCardProps {
  isActive: boolean;
  isBusy: boolean;
  onUseHosted: () => void;
}

function HostedProviderCard({ isActive, isBusy, onUseHosted }: HostedProviderCardProps) {
  return (
    <div className="rounded-lg border border-border/60 p-4 space-y-4 h-full flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Cloud className="h-4 w-4 text-primary" />
          <p className="text-sm font-medium text-foreground">Agentic Flow (hosted)</p>
        </div>
        {isActive ? <Badge>Active</Badge> : null}
      </div>

      <p className="text-xs text-muted-foreground">
        Runs on {HOSTED_MODEL_LABEL}, shared across the workspace and included in your plan's usage
        cap. No setup required.
      </p>

      <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 px-3 py-2.5 space-y-1">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 [text-transform:var(--eyebrow-transform,uppercase)] [letter-spacing:var(--eyebrow-tracking,0.05em)]">
          <Coins className="h-3.5 w-3.5" /> Our rate
        </div>
        <p className="text-sm text-foreground">
          ${HOSTED_MODEL_PRICING.inputPerMillionUsd.toFixed(3)} / 1M input tokens
        </p>
        <p className="text-sm text-foreground">
          ${HOSTED_MODEL_PRICING.outputPerMillionUsd.toFixed(2)} / 1M output tokens
        </p>
      </div>

      <div className="flex-1" />

      {isActive ? (
        <p className="text-xs text-muted-foreground">This is your workspace's current source.</p>
      ) : (
        <Button disabled={isBusy} onClick={onUseHosted} size="sm" variant="outline">
          Use hosted key instead
        </Button>
      )}
    </div>
  );
}

interface ByokPanelProps {
  savedKeys: SavedKey[];
  isLoading: boolean;
  isBusy: boolean;
  isSaving: boolean;
  savingProvider: AiProvider | undefined;
  onSave: (provider: AiProvider, apiKey: string) => void;
  onSetActive: (provider: AiProvider) => void;
  onRemove: (provider: AiProvider) => void;
}

function ByokPanel({
  savedKeys,
  isLoading,
  isBusy,
  isSaving,
  savingProvider,
  onSave,
  onSetActive,
  onRemove,
}: ByokPanelProps) {
  const hasActive = savedKeys.some((key) => key.isActive);

  return (
    <div className="rounded-lg border border-border/60 p-4 space-y-4 h-full flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <p className="text-sm font-medium text-foreground">Bring your own key</p>
        </div>
        {hasActive ? <Badge>Active</Badge> : null}
      </div>

      <p className="text-xs text-muted-foreground">
        Your calls skip the hosted usage cap entirely and the provider bills you directly.
      </p>

      {!isLoading && savedKeys.length > 0 && (
        <div className="space-y-2">
          {savedKeys.map((key) => (
            <SavedKeyRow
              key={key.provider}
              savedKey={key}
              isBusy={isBusy}
              onSetActive={() => onSetActive(key.provider)}
              onRemove={() => onRemove(key.provider)}
            />
          ))}
        </div>
      )}

      <div className="flex-1" />

      <AddKeyForm
        isSaving={isSaving}
        savingProvider={savingProvider}
        savedProviders={savedKeys.map((key) => key.provider)}
        onSave={onSave}
      />
    </div>
  );
}

interface SavedKeyRowProps {
  savedKey: SavedKey;
  isBusy: boolean;
  onSetActive: () => void;
  onRemove: () => void;
}

function SavedKeyRow({ savedKey, isBusy, onSetActive, onRemove }: SavedKeyRowProps) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-border/50 bg-background/60 px-3 py-2">
      <div className="min-w-0">
        <p className="text-sm text-foreground truncate">{AI_PROVIDER_LABELS[savedKey.provider]}</p>
        <p className="text-xs text-muted-foreground truncate">
          Key ending in ...{savedKey.keyLast4}
          {savedKey.connectedByEmail ? ` · saved by ${savedKey.connectedByEmail}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {savedKey.isActive ? (
          <Badge variant="secondary">Active</Badge>
        ) : (
          <Button disabled={isBusy} onClick={onSetActive} size="sm" variant="outline">
            Make active
          </Button>
        )}
        <Button disabled={isBusy} onClick={onRemove} size="sm" variant="ghost">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

interface AddKeyFormProps {
  isSaving: boolean;
  savingProvider: AiProvider | undefined;
  savedProviders: AiProvider[];
  onSave: (provider: AiProvider, apiKey: string) => void;
}

function AddKeyForm({ isSaving, savingProvider, savedProviders, onSave }: AddKeyFormProps) {
  const firstUnclaimed = AI_PROVIDERS.find((provider) => !savedProviders.includes(provider));
  const [provider, setProvider] = useState<AiProvider>(firstUnclaimed ?? AI_PROVIDERS[0]);
  const [apiKey, setApiKey] = useState('');
  const alreadySaved = savedProviders.includes(provider);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!apiKey.trim()) return;
    onSave(provider, apiKey.trim());
    setApiKey('');
  };

  return (
    <form className="space-y-2" onSubmit={submit}>
      <div className="flex items-center gap-2">
        <Select
          value={provider}
          onValueChange={(value) => setProvider(value as AiProvider)}
          disabled={isSaving}
        >
          <SelectTrigger className="w-full" size="sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AI_PROVIDERS.map((option) => (
              <SelectItem key={option} value={option}>
                {AI_PROVIDER_LABELS[option]}
                {savedProviders.includes(option) ? ' (saved)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-2">
        <PasswordInput
          className="flex-1"
          disabled={isSaving}
          onChange={(event) => setApiKey(event.target.value)}
          placeholder={`${AI_PROVIDER_LABELS[provider]} API key`}
          value={apiKey}
        />
        <Button disabled={isSaving || !apiKey.trim()} size="sm" type="submit">
          {isSaving && savingProvider === provider ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : null}
          Save
        </Button>
      </div>
      {alreadySaved && (
        <p className="text-xs text-muted-foreground">
          Replaces the {AI_PROVIDER_LABELS[provider]} key already on file.
        </p>
      )}
    </form>
  );
}
